/**
 * Left panel of the View tab: the case tree.
 *
 * The tree is a view over the six `CaseNode` arrays in the manifest, not a
 * second data model. The mockup's branch names are clinician-facing (Images /
 * Overlays / Derived), so the mapping lives in `viewerStore`'s
 * `NODE_GROUP_LABELS`; the QC branch is derived, not stored — see `viewer/qc`.
 */

import * as React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  MinusCircle,
  MoreHorizontal,
  RefreshCw,
  Search,
  User,
} from "lucide-react";
import { Checkbox, EmptyState, Input, Panel } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { getResultsSummary, openPath, readNiftiHeader, refreshCaseNodes, revealPath, splitError } from "@/ipc/api";
import { copyText } from "@/results/clipboard";
import type { CaseNode, ResultsSummary } from "@/ipc/contract";
import { appActions, useAppStore } from "@/store/appStore";
import { selectTool } from "@/tools/actions";
import { formatAffine } from "@/tools/header";
import {
  nodeAbsPath,
  useManifest,
  useViewerStore,
  type NodeGroup,
} from "@/viewer/viewerStore";
import { deriveQcReport, type CheckStatus } from "@/viewer/qc";
import { buildTree, fileName, openActionFor, openLabelFor, shortName } from "./studyTree";

/** Groups the mockup shows expanded; the rest start collapsed to keep it dense. */
const DEFAULT_OPEN: Record<string, boolean> = {
  images: true,
  masks: true,
  transforms: true,
  qc: true,
};

/** Kind -> the colour that used to identify its group. */
function groupOfKind(kind: CaseNode["kind"]): NodeGroup {
  switch (kind) {
    case "mask":
      return "masks";
    case "transform":
      return "transforms";
    case "atlas":
      return "atlases";
    case "table":
      return "measurements";
    case "report":
    case "figure":
      return "reports";
    default:
      return "images";
  }
}

const GROUP_DOT: Record<NodeGroup | "qc", string> = {
  images: "bg-neutral-700",
  masks: "bg-red-500",
  transforms: "bg-sky-500",
  atlases: "bg-violet-500",
  measurements: "bg-emerald-500",
  reports: "bg-amber-500",
  qc: "bg-neutral-400",
};

export function StudyExplorer() {
  const manifest = useManifest();
  const selectedNodeId = useAppStore((s) => s.selectedNodeId);
  const selectedNodeIds = useAppStore((s) => s.selectedNodeIds ?? []);
  const store = useViewerStore();
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState<Record<string, boolean>>(DEFAULT_OPEN);
  const [menu, setMenu] = React.useState<{ node: CaseNode; x: number; y: number } | null>(null);
  const [results, setResults] = React.useState<ResultsSummary | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);

  // QC branch content needs the results summary; failures are non-fatal because
  // the tree is still usable without it.
  React.useEffect(() => {
    if (!manifest) {
      setResults(null);
      return;
    }
    let cancelled = false;
    void getResultsSummary(manifest.caseId)
      .then((summary) => !cancelled && setResults(summary))
      .catch(() => !cancelled && setResults(null));
    return () => {
      cancelled = true;
    };
  }, [manifest]);

  const qc = React.useMemo(() => deriveQcReport(manifest, results), [manifest, results]);

  const matches = React.useCallback(
    (label: string) => label.toLowerCase().includes(query.trim().toLowerCase()),
    [query],
  );

  const folders = React.useMemo(() => (manifest ? buildTree(manifest) : []), [manifest]);

  // Shift-click ranges follow what is on screen, so this has to be the tree's
  // own order rather than the manifest's.
  const visibleIds = React.useMemo(
    () =>
      folders.flatMap((folder) =>
        folder.nodes
          .filter((node) => matches(fileName(node)) || matches(node.label))
          .map((node) => node.id),
      ),
    [folders, matches],
  );

  const toggleGroup = (id: string) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  /** The one "open" action. See `openLabelFor` for why this exists. */
  const activate = React.useCallback(
    (node: CaseNode) => {
      appActions.setSelectedNodeId(node.id);
      switch (openActionFor(node.kind).kind) {
        case "base":
          store.setBaseNode(node.id);
          appActions.setActiveTab("view");
          break;
        case "overlay":
          if (!store.overlayNodeIds.includes(node.id)) store.toggleOverlayNode(node.id);
          appActions.setActiveTab("view");
          break;
        case "external": {
          const path = manifest ? nodeAbsPath(manifest, node) : null;
          if (path) void openPath(path).catch(() => undefined);
          break;
        }
        default:
          // Transforms and the like have no viewer; selecting them is the
          // whole action, and the Inspector is already showing them.
          appActions.togglePanel("right", false);
      }
    },
    [manifest, store],
  );

  const onRefresh = React.useCallback(async () => {
    if (!manifest) return;
    setRefreshing(true);
    try {
      appActions.setCurrentCase(await refreshCaseNodes(manifest.caseId));
    } catch (error) {
      appActions.notify({
        title: "Could not refresh the case",
        detail: splitError(error).message,
        tone: "error",
      });
    } finally {
      setRefreshing(false);
    }
  }, [manifest]);

  if (!manifest) {
    return (
      <Panel title="Explorer" className="h-full">
        <EmptyState
          title="No case open"
          hint="Open a case from the Home tab to browse its images, overlays and derived files."
        />
      </Panel>
    );
  }

  return (
    <Panel
      title="Study Explorer"
      className="h-full"
      actions={
        <button
          type="button"
          title="Rescan the output directory"
          aria-label="Rescan the output directory"
          onClick={() => void onRefresh()}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-ads-muted hover:bg-ads-hover"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
        </button>
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="relative shrink-0 px-2 py-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ads-faint" />
          <Input
            className="h-7 pl-7 text-xs"
            placeholder="Search…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-auto pb-2 text-xs">
          <div className="flex items-center gap-1.5 px-2 py-1 font-medium text-ads-text">
            <User className="h-3.5 w-3.5 text-ads-muted" aria-hidden />
            {manifest.subjectId}
          </div>
          <div className="px-2 pb-1 pl-7 text-[11px] text-ads-muted">{manifest.studyName}</div>

          {folders.map((folder) => {
            const nodes = folder.nodes.filter(
              (node) => matches(fileName(node)) || matches(node.label),
            );
            if (nodes.length === 0) return null;
            const isOpen = open[folder.id] ?? true;
            return (
              <div key={folder.id}>
                <GroupRow
                  label={folder.label}
                  count={folder.nodes.length}
                  dot="bg-neutral-400"
                  open={isOpen}
                  onToggle={() => toggleGroup(folder.id)}
                />
                {isOpen
                  ? nodes.map((node) => (
                      <NodeRow
                        key={node.id}
                        node={node}
                        name={shortName(node, manifest.subjectId)}
                        group={groupOfKind(node.kind)}
                        selected={selectedNodeIds.includes(node.id) || node.id === selectedNodeId}
                        isBase={store.baseNodeId === node.id}
                        isOverlay={store.overlayNodeIds.includes(node.id)}
                        onSelect={(event) => {
                          const mode = event.shiftKey
                            ? "range"
                            : event.metaKey || event.ctrlKey
                              ? "toggle"
                              : "replace";
                          appActions.selectNode(node.id, mode, visibleIds);
                        }}
                        onActivate={() => activate(node)}
                        onSetBase={() => {
                          store.setBaseNode(node.id);
                          // The radio stops propagation so the row's own click
                          // never fires. Without this the Inspector keeps
                          // describing whatever was highlighted before, and you
                          // get the volume you asked for next to the details of
                          // a different one.
                          appActions.selectNode(node.id, "replace", visibleIds);
                        }}
                        onToggleOverlay={() => store.toggleOverlayNode(node.id)}
                        onMenu={(x, y) => setMenu({ node, x, y })}
                      />
                    ))
                  : null}
              </div>
            );
          })}

          <GroupRow
            label="QC"
            dot={GROUP_DOT.qc}
            open={open.qc ?? false}
            onToggle={() => toggleGroup("qc")}
          />
          {open.qc
            ? qc.checks
                .filter((check) => matches(check.label))
                .map((check) => (
                  <button
                    key={check.id}
                    type="button"
                    title={check.detail}
                    onClick={() => {
                      appActions.setDockTab("qc");
                      appActions.togglePanel("bottom", false);
                    }}
                    className="flex w-full items-center gap-1.5 py-[3px] pl-9 pr-2 text-left hover:bg-ads-hover"
                  >
                    <StatusIcon status={check.status} />
                    <span
                      className={cn(
                        "truncate",
                        check.status === "not_computed" ? "text-ads-faint italic" : "text-ads-text",
                      )}
                    >
                      {check.label}
                    </span>
                  </button>
                ))
            : null}
        </div>
      </div>

      {menu ? (
        <ContextMenu
          node={menu.node}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          onView={() => {
            if (menu.node.kind === "image") store.setBaseNode(menu.node.id);
            else if (!store.overlayNodeIds.includes(menu.node.id)) store.toggleOverlayNode(menu.node.id);
            appActions.setActiveTab("view");
          }}
          onProperties={() => {
            appActions.setSelectedNodeId(menu.node.id);
            appActions.togglePanel("right", false);
          }}
          onOpenFolder={() => {
            const path = nodeAbsPath(manifest, menu.node);
            if (path) void revealPath(path).catch(() => undefined);
          }}
          onToggleOverlay={() => store.toggleOverlayNode(menu.node.id)}
          onCopyPath={() => {
            const path = nodeAbsPath(manifest, menu.node);
            if (path) void copyText(path);
          }}
          onHeader={() => {
            appActions.setSelectedNodeId(menu.node.id);
            selectTool("prep.header");
          }}
          onCopyAffine={() => {
            const path = nodeAbsPath(manifest, menu.node);
            if (!path) return;
            void readNiftiHeader(path)
              .then((info) => copyText(formatAffine(info.affine) || "No affine in this header."))
              .catch(() => undefined);
          }}
          onOpenResult={() => {
            const path = nodeAbsPath(manifest, menu.node);
            if (path) void openPath(path).catch(() => undefined);
          }}
        />
      ) : null}
    </Panel>
  );
}

function GroupRow({
  label,
  dot,
  open,
  onToggle,
  count,
}: {
  label: string;
  dot: string;
  open: boolean;
  onToggle: () => void;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-1 px-2 py-[3px] text-left font-medium text-ads-text hover:bg-ads-hover"
    >
      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      <span className={cn("h-2 w-2 rounded-full", dot)} aria-hidden />
      <span className="min-w-0 truncate">{label}</span>
      {count != null ? <span className="ml-auto pl-2 text-[11px] text-ads-faint">{count}</span> : null}
    </button>
  );
}

function NodeRow({
  node,
  name,
  group,
  selected,
  isBase,
  isOverlay,
  onSelect,
  onActivate,
  onSetBase,
  onToggleOverlay,
  onMenu,
}: {
  node: CaseNode;
  /** The filename, minus the subject-id prefix every file repeats. */
  name: string;
  group: NodeGroup;
  selected: boolean;
  isBase: boolean;
  isOverlay: boolean;
  onSelect: (event: React.MouseEvent) => void;
  onActivate: () => void;
  onSetBase: () => void;
  onToggleOverlay: () => void;
  onMenu: (x: number, y: number) => void;
}) {
  // Only a 3D-ish volume can serve as the base; tables and reports cannot be
  // drawn, so they get neither control.
  const canBeBase = node.kind === "image";
  const canOverlay = node.kind === "image" || node.kind === "mask" || node.kind === "atlas";

  return (
    <div
      role="treeitem"
      aria-selected={selected}
      tabIndex={0}
      onClick={onSelect}
      onDoubleClick={(event) => {
        event.preventDefault();
        onActivate();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActivate();
        }
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onSelect(event);
        onMenu(event.clientX, event.clientY);
      }}
      className={cn(
        "group flex cursor-default items-center gap-1.5 py-[3px] pl-6 pr-1.5",
        selected ? "bg-ads-accent-soft" : "hover:bg-ads-hover",
      )}
      title={`${node.relPath}\n${node.label}\n\nDouble-click: ${openLabelFor(node.kind).toLowerCase()}`}
    >
      {canBeBase ? (
        <input
          type="radio"
          name="study-explorer-base"
          className="h-3 w-3 shrink-0 accent-ads-accent"
          checked={isBase}
          onChange={onSetBase}
          onClick={(event) => event.stopPropagation()}
          title="Show as the base volume"
          aria-label={`Show ${node.label} as the base volume`}
        />
      ) : (
        <span className={cn("h-2 w-2 shrink-0 rounded-sm", GROUP_DOT[group])} aria-hidden />
      )}

      {/* The filename is the identity: it is unique, and it is what you would
          search for in Finder. The derived label rides along muted, because it
          is a description and four files can share one. */}
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="min-w-0 truncate text-ads-text">{name}</span>
        <span className="shrink-0 truncate text-[10.5px] text-ads-faint">{node.label}</span>
      </span>

      <button
        type="button"
        title="Node actions"
        aria-label={`Actions for ${node.label}`}
        onClick={(event) => {
          event.stopPropagation();
          onMenu(event.clientX, event.clientY);
        }}
        className="hidden h-4 w-4 shrink-0 items-center justify-center rounded text-ads-muted hover:bg-ads-active group-hover:flex"
      >
        <MoreHorizontal className="h-3 w-3" />
      </button>

      {canOverlay ? (
        <Checkbox
          className="shrink-0"
          checked={isOverlay}
          onChange={onToggleOverlay}
          onClick={(event) => event.stopPropagation()}
          title="Draw as an overlay"
          aria-label={`Draw ${node.label} as an overlay`}
        />
      ) : (
        /* A table, report or figure has no overlay checkbox, which used to
           leave it with no control at all and nothing to click. This is the
           one it should have had: open it. */
        <button
          type="button"
          title={openLabelFor(node.kind)}
          aria-label={`${openLabelFor(node.kind)}: ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onActivate();
          }}
          className="flex h-4 w-3.5 shrink-0 items-center justify-center rounded text-ads-faint opacity-0 hover:bg-ads-active hover:text-ads-text group-hover:opacity-100"
        >
          <ChevronRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: CheckStatus }) {
  const common = "h-3.5 w-3.5 shrink-0";
  if (status === "pass") return <CheckCircle2 className={cn(common, "text-green-600")} aria-label="pass" />;
  if (status === "warning")
    return <AlertTriangle className={cn(common, "text-amber-500")} aria-label="warning" />;
  if (status === "unavailable")
    return <MinusCircle className={cn(common, "text-ads-faint")} aria-label="not available" />;
  return <CircleDashed className={cn(common, "text-ads-faint")} aria-label="not computed" />;
}

function ContextMenu({
  node,
  x,
  y,
  onClose,
  onView,
  onProperties,
  onOpenFolder,
  onToggleOverlay,
  onCopyPath,
  onHeader,
  onCopyAffine,
  onOpenResult,
}: {
  node: CaseNode;
  x: number;
  y: number;
  onClose: () => void;
  onView: () => void;
  onProperties: () => void;
  onOpenFolder: () => void;
  onToggleOverlay: () => void;
  onCopyPath: () => void;
  onHeader: () => void;
  onCopyAffine: () => void;
  onOpenResult: () => void;
}) {
  React.useEffect(() => {
    const dismiss = () => onClose();
    // `click` (not mousedown) so the item's own handler runs first.
    document.addEventListener("click", dismiss);
    document.addEventListener("contextmenu", dismiss);
    return () => {
      document.removeEventListener("click", dismiss);
      document.removeEventListener("contextmenu", dismiss);
    };
  }, [onClose]);

  const viewable = node.kind === "image" || node.kind === "mask" || node.kind === "atlas";
  const overlayable = node.kind === "image" || node.kind === "mask" || node.kind === "atlas";
  const isResult = node.kind === "table" || node.kind === "report" || node.kind === "figure";

  return (
    <div
      role="menu"
      style={{ left: x, top: y }}
      className="fixed z-50 min-w-40 rounded border border-ads-border-strong bg-ads-surface py-1 text-xs shadow-lg"
    >
      {node.kind === "image" ? <MenuItem label="View as base" onClick={onView} /> : null}
      {overlayable ? <MenuItem label="Toggle overlay" onClick={onToggleOverlay} /> : null}
      {viewable && node.kind !== "image" ? <MenuItem label="View" onClick={onView} /> : null}
      {isResult ? <MenuItem label="Open result" onClick={onOpenResult} /> : null}
      <MenuItem label="Properties" onClick={onProperties} />
      {viewable ? <MenuItem label="NIfTI Header" onClick={onHeader} /> : null}
      {viewable ? <MenuItem label="Copy affine" onClick={onCopyAffine} /> : null}
      <MenuItem label="Copy path" onClick={onCopyPath} />
      <MenuItem label="Reveal in folder" onClick={onOpenFolder} />
    </div>
  );
}

function MenuItem({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="block w-full px-3 py-1 text-left text-ads-text hover:bg-ads-hover disabled:cursor-default disabled:text-ads-faint disabled:hover:bg-transparent"
    >
      {label}
    </button>
  );
}
